import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, withHandlers, branch, renderComponent } from 'recompose';
import { ViewportAction } from '@sparefoot/react-core';

import pluralize from 'utils/pluralize';
import { withSelfStorageSchema } from 'decorators/search';

import { H3, Paragraph, Text } from 'components/core/Typography';
import { Link } from 'components/core/Link';
import { Image } from 'components/core/Image';
import { Button } from 'components/core/Button';

import { FacilityImage } from 'components/facility/FacilityImage';
import { ClickToCall } from 'components/facility/ClickToCall';
import { BlueStarRating } from 'components/review/StarRating';
import { NewStackedUnits } from 'components/unit/StackedUnits';
import { LoadingFacilityCard } from 'components/facility/FacilityCard/LoadingFacilityCard';

import ChevronRight from 'assets/images/icons/chevron-right.svg';
import Close from 'assets/images/icons/close.svg';

import './NewFacilityCard.scss';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';

export const enhance = compose(
	withHandlers({
		handleFetchNumber:
			({ getFacilityCardPhoneNumber }) =>
			() =>
				getFacilityCardPhoneNumber(),
		handleDeselect:
			({ handleFacilityDeselected, handlePinDeselected }) =>
			() => {
				handleFacilityDeselected();
				handlePinDeselected();
			},
		handleSelect:
			({
				handleFacilitySelected,
				handlePinDeselected,
				facility,
				miniCard,
			}) =>
			() => {
				if (!miniCard) {
					// if a normal facility card is selected,
					// it should select the pin but without showing the mini card on the map
					handleFacilitySelected(facility.id);
					handlePinDeselected();
				}
			},
		handleTrackCTA: ({ facility }) => {
			trackCTAClick(
				'view-all-units',
				'View all units/prices Button',
				'Compare all units at this facility',
				facility.url,
			);
		},
	}),
	branch(({ loading }) => loading, renderComponent(LoadingFacilityCard)),
	withSelfStorageSchema,
);

export function NewFacilityCard({
	bottomMap,
	facility,
	baseUrl,
	facilitySelected,
	handleDeselect,
	handleSelect,
	handleTrackCTA,
	handleFetchNumber,
	lazyLoad,
	miniCard,
	onVisible,
	percentVisible,
	storageType,
	searchParams,
	selfStorageSchema,
	withSchema,
}) {
	return (
		<ViewportAction
			onVisible={onVisible}
			percentVisible={percentVisible}
		>
			<div
				className={classnames(
					'new-facility-card',
					'facility-card-loaded',
					{ selected: facility.id === facilitySelected },
					{ miniCard },
					{ bottomMap },
				)}
				onMouseEnter={handleSelect}
				itemScope
				itemType="https://schema.org/LocalBusiness"
			>
				{miniCard && (
					<Button
						className="close-bottom-map-card"
						onClick={handleDeselect}
					>
						<Image
							src={Close}
							alt="close card"
						/>
					</Button>
				)}
				<div className="facility-head">
					<Link
						href={`${baseUrl}${facility.url}`}
						productClick
					>
						<div
							className="image-wrapper"
							itemProp="image"
							itemScope
							itemType="https://schema.org/ImageObject"
						>
							<FacilityImage
								itemProp="url"
								src={facility.images[0]}
								alt={`Photo of ${facility.name}`}
								size="medium"
								lazyLoad={lazyLoad}
							/>
						</div>
					</Link>
					<div className="facility-details">
						<Link
							href={`${baseUrl}${facility.url}`}
							productClick
						>
							<H3
								className="facility-name"
								color="black"
								last
								itemProp="name"
							>
								{facility.name}
							</H3>
							<Paragraph
								className="facility-location"
								itemProp="address"
								itemScope
								itemType="https://schema.org/PostalAddress"
							>
								{`${facility.distance} miles away ${facility.location.city} ${facility.location.state} ${facility.location.zip}`}
							</Paragraph>
						</Link>
						<div className="call-n-reviews">
							<ClickToCall
								phone={facility.phone}
								onFetchNumber={handleFetchNumber}
							/>
							{!!facility.reviews.number && (
								<span className="facility-rating">
									<BlueStarRating
										rating={facility.reviews.average}
									/>
									<Link
										className="test"
										href={`${baseUrl}${facility.url}#facility-page-user-reviews`}
									>
										<Text
											className="num-rating"
											size="tiny"
											color="blue"
											last
										>
											{facility.reviews.average}
										</Text>
									</Link>
								</span>
							)}
						</div>
					</div>
				</div>
				<NewStackedUnits
					facility={facility}
					baseUrl={baseUrl}
					units={facility.units}
					storageType={storageType}
					searchParams={searchParams}
					withScarcity
				/>
				<Link
					className="compare-units"
					href={`${baseUrl}${facility.url}`}
					onClick={handleTrackCTA}
					productClick
				>
					<Paragraph
						size="smallMedium"
						weight="medium"
						color="blue"
						last
					>
						Compare all{' '}
						<Text
							size="smallMedium"
							weight="extraBold"
							color="blue"
						>
							{facility.units.unitsAvailable}{' '}
							{pluralize('unit', facility.units.unitsAvailable)}{' '}
						</Text>{' '}
						at this facility{' '}
						<Image
							src={ChevronRight}
							alt=""
							aria-hidden="true"
						/>
					</Paragraph>
				</Link>
			</div>
			{withSchema && (
				<script
					key="schema"
					type="application/ld+json"
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{ __html: selfStorageSchema }}
				/>
			)}
		</ViewportAction>
	);
}

NewFacilityCard.propTypes = {
	bottomMap: PropTypes.bool,
	facility: PropTypes.object.isRequired,
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	handleDeselect: PropTypes.func,
	handleSelect: PropTypes.func,
	handleTrackCTA: PropTypes.func,
	handleFetchNumber: PropTypes.func.isRequired,
	miniCard: PropTypes.bool,
	lazyLoad: PropTypes.bool,
	onVisible: PropTypes.func.isRequired,
	percentVisible: PropTypes.number,
	searchParams: PropTypes.object,
	selfStorageSchema: PropTypes.string,
	storageType: PropTypes.oneOf(['storage', 'vehicle']),
	withSchema: PropTypes.bool,
};

NewFacilityCard.defaultProps = {
	storageType: 'storage',
};

export default enhance(NewFacilityCard);
